import { useState, useCallback, useMemo, useEffect } from 'react';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import type {
  DocumentReference,
  Binary,
  Bundle,
  Resource,
} from '@medplum/fhirtypes';
import { OperationOutcomeError } from '@medplum/core';
import { v4 as uuidv4 } from 'uuid';
import type { MedicalRecordLocal, MedicalRecordsData } from '../types/medicalRecords';
import { PATIENT_UPLOAD_CODE, AI_CATEGORIZED_EXTENSION_URL, resolveDocumentCategory } from '../types/medicalRecords';
import { onboardingService, type MedicalRecordsReviewed } from '../services/onboardingService';

// LOINC codes used by logistics documents - exclude these from medical records
const LOGISTICS_DOCUMENT_CODES = ['53245-7', '64290-0', '64291-8'];

// Extract MedicalRecordsData from FHIR resources
export function extractMedicalRecordsData(
  documentReferences: DocumentReference[],
  medplumBaseUrl: string
): MedicalRecordsData {
  const records: MedicalRecordLocal[] = [];

  for (const docRef of documentReferences) {
    // Skip logistics documents (ID cards, insurance cards)
    const typeCode = docRef.type?.coding?.[0]?.code;
    if (typeCode && LOGISTICS_DOCUMENT_CODES.includes(typeCode)) {
      continue;
    }

    const attachment = docRef.content?.[0]?.attachment;
    if (!attachment?.url) continue;

    // Handle both relative (Binary/xxx) and absolute URLs
    const attachmentUrl = attachment.url;
    const isAbsoluteUrl = attachmentUrl.startsWith('http');

    // Extract binary ID from URL (format: "Binary/xxx" or full URL)
    const binaryId = isAbsoluteUrl
      ? attachmentUrl.split('/').pop() || attachmentUrl
      : attachmentUrl.replace('Binary/', '');

    // Use the URL directly if it's absolute, otherwise construct it
    const viewUrl = isAbsoluteUrl
      ? attachmentUrl
      : `${medplumBaseUrl}Binary/${binaryId}`;

    records.push({
      localId: uuidv4(),
      fhirId: docRef.id,
      binaryId,
      filename: attachment.title || 'Untitled',
      contentType: attachment.contentType || 'application/octet-stream',
      uploadedAt: docRef.date || new Date().toISOString(),
      url: viewUrl,
      documentCategory: resolveDocumentCategory(typeCode),
    });
  }

  // Sort by upload date, newest first
  records.sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime());

  return { records };
}

export function useMedicalRecords() {
  const medplum = useMedplum();
  const baseProfile = useMedplumProfile() as Resource | undefined;

  const [documentReferences, setDocumentReferences] = useState<DocumentReference[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reviewedFlags, setReviewedFlags] = useState<MedicalRecordsReviewed>(
    onboardingService.getMedicalRecordsReviewed()
  );

  const patientId = baseProfile?.id;
  const resourceType = (baseProfile as { resourceType?: string })?.resourceType;
  const medplumBaseUrl = medplum.getBaseUrl();

  // Load existing data from Medplum
  const loadData = useCallback(async () => {
    if (!patientId || resourceType !== 'Patient') {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Fetch all DocumentReferences for this patient
      try {
        const result = await medplum.search('DocumentReference', {
          subject: `Patient/${patientId}`,
          status: 'current',
          _sort: '-date',
        });
        const bundle = result as Bundle<DocumentReference>;
        setDocumentReferences(
          bundle.entry?.map((e) => e.resource as DocumentReference) || []
        );
      } catch {
        // No documents found - that's okay
        setDocumentReferences([]);
      }
    } catch (err) {
      console.error('Failed to load medical records:', err);
      if (err instanceof OperationOutcomeError) {
        console.error('FHIR OperationOutcome:', JSON.stringify(err.outcome, null, 2));
      }
      setError('Unable to load medical records. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [medplum, patientId, resourceType]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Extract to local format
  const medicalRecordsData = useMemo(
    () => extractMedicalRecordsData(documentReferences, medplumBaseUrl),
    [documentReferences, medplumBaseUrl]
  );

  // Data status
  const hasRecords = medicalRecordsData.records.length > 0;

  // Section is complete if: has data OR marked as "no content"
  const isComplete = hasRecords || reviewedFlags.noRecords;

  // Update reviewed flags
  const setReviewedFlag = useCallback((flag: keyof MedicalRecordsReviewed, value: boolean) => {
    const updated = onboardingService.setMedicalRecordsReviewed({ [flag]: value });
    setReviewedFlags(updated);
  }, []);

  // Upload multiple files
  const uploadFiles = useCallback(
    async (files: File[]): Promise<boolean> => {
      if (!patientId) {
        setError('Patient data not loaded');
        return false;
      }

      try {
        setIsSaving(true);
        setError(null);

        for (const file of files) {
          // Create Binary resource with file data
          const binary = await medplum.createBinary({
            data: file,
            filename: file.name,
            contentType: file.type,
          });

          const typeCode = PATIENT_UPLOAD_CODE;

          // Create DocumentReference
          const docRef: DocumentReference = {
            resourceType: 'DocumentReference',
            status: 'current',
            type: {
              coding: [typeCode],
              text: typeCode.display,
            },
            subject: { reference: `Patient/${patientId}` },
            date: new Date().toISOString(),
            content: [
              {
                attachment: {
                  url: `Binary/${(binary as Binary).id}`,
                  contentType: file.type,
                  title: file.name,
                },
              },
            ],
            // Extension to track AI categorization status
            extension: [
              {
                url: AI_CATEGORIZED_EXTENSION_URL,
                valueBoolean: false,
              },
            ],
          };

          await medplum.createResource(docRef);
        }

        // Reload data to get updated list
        await loadData();
        return true;
      } catch (err) {
        console.error('Failed to upload medical records:', err);
        if (err instanceof OperationOutcomeError) {
          console.error('FHIR OperationOutcome:', JSON.stringify(err.outcome, null, 2));
        }
        setError('Unable to upload files. Please try again.');
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [medplum, patientId, loadData]
  );

  // Delete a single record
  const deleteRecord = useCallback(
    async (fhirId: string): Promise<boolean> => {
      try {
        setIsSaving(true);
        setError(null);

        await medplum.deleteResource('DocumentReference', fhirId);
        await loadData();
        return true;
      } catch (err) {
        console.error('Failed to delete medical record:', err);
        if (err instanceof OperationOutcomeError) {
          console.error('FHIR OperationOutcome:', JSON.stringify(err.outcome, null, 2));
        }
        setError('Unable to delete record. Please try again.');
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [medplum, loadData]
  );

  return {
    medicalRecordsData,
    isLoading,
    isSaving,
    error,
    isComplete,
    hasRecords,
    // Reviewed flags
    reviewedFlags,
    setReviewedFlag,
    // Actions
    uploadFiles,
    deleteRecord,
    refresh: loadData,
  };
}
